import { useState } from "react";
import { AdminOrdersEndpoints } from "@olagg/api-hooks";
import { Order } from "@olagg/db-types";
import { fetcher } from "@olagg/api-hooks";
import { useToast } from "@chakra-ui/react";

export interface OrdersFilter {
  page: number;
  limit: number;
  order_by?: string;
  user_name?:string;
  status?: string;
}

const useOrders = () => {
  const toast = useToast();
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [count, setCount] = useState<number>(0);

  const all = async (filter: OrdersFilter) => {
    setIsLoading(true);
    try {
      const response = await fetcher(AdminOrdersEndpoints.all(filter));
      setCount(response.meta?.count || 0);
      setOrders(response.orders || response);
      setIsLoading(false);
    } catch (error: any) {
      toast({
        title: 'Error',
        colorScheme: 'olaggYellow',
        status: 'error',
        description: error.message || 'Error al cargar órdenes',
        position: 'bottom-right'
      });
      setIsLoading(false);
    }
  }

  const refundOrder = async (orderId: string) => {
    try {
      await fetcher(AdminOrdersEndpoints.refund(orderId));
      toast({
        title: 'Reembolso procesado',
        description: 'El reembolso ha sido procesado exitosamente',
        status: 'success',
        duration: 3000,
        position: 'top-right',
      });
      return true;
    } catch (error: any) {
      toast({
        title: 'Error en el reembolso',
        colorScheme: 'olaggYellow',
        status: 'error',
        description: error.message || 'Error al procesar el reembolso',
        position: 'bottom-right'
      });
      return false;
    }
  }

  const retryBonoxOrder = async (orderId: string) => {
    try {
      await fetcher(AdminOrdersEndpoints.retryBonox(orderId));
      toast({
        title: 'Orden reintentada',
        description: 'La orden ha sido procesada exitosamente',
        status: 'success',
        duration: 3000,
        position: 'top-right',
      });
      return true;
    } catch (error: any) {
      toast({
        title: 'Error al reintentar',
        colorScheme: 'olaggYellow',
        status: 'error',
        description: error.message || 'Error al reintentar la orden',
        position: 'bottom-right'
      });
      return false;
    }
  }

  const verifyPayment = async (orderId: string) => {
    try {
      await fetcher(AdminOrdersEndpoints.retryPayment(orderId));
      toast({
        title: 'Pago verificado',
        description: 'El pago ha sido verificado exitosamente',
        status: 'success',
        duration: 3000,
        position: 'top-right',
      });
      return true;
    } catch (error: any) {
      toast({
        title: 'Error al verificar pago',
        colorScheme: 'olaggYellow',
        status: 'error',
        description: error.errors || 'Error al verificar el pago',
        position: 'bottom-right'
      });
      return false;
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return '#38A169'; // Verde
      case 'pending':
        return '#D69E2E'; // Amarillo
      case 'processing':
        return '#3182CE'; // Azul
      case 'error':
        return '#E53E3E'; // Rojo
      case 'for_revision':
        return '#ED8936'; // Naranja
      case 'refunded':
        return '#805AD5'; // Morado
      default:
        return '#718096'; // Gris
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'success':
        return 'Completada';
      case 'pending':
        return 'Pendiente';
      case 'processing':
        return 'Procesando';
      case 'error':
        return 'Error';
      case 'for_revision':
        return 'En Revisión';
      case 'refunded':
        return 'Reembolsada';
      default:
        return status;
    }
  }

  return {
    orders,
    isLoading,
    count,
    all,
    refundOrder,
    retryBonoxOrder,
    verifyPayment,
    getStatusColor,
    getStatusText
  }
}

export default useOrders;